/**
 * Setup — the onboarding front door. WIRED to GET /api/setup, whose every step's status is
 * computed from real data (a credential that exists, a number that is active, a contact that
 * replied YES), not a stored flag. Each row links to where the step is actually done. Going live
 * has its own harder gate on the Settings screen; this is the guided path to being ready for it.
 */
import { Link } from "react-router-dom";
import { Badge, Button, Icon, Notice, PageHeader, Panel } from "../components/ui";
import { api } from "../../lib/api.ts";
import type { SetupResponse, SetupStep } from "../../lib/api.ts";
import { qk, useResource } from "../../lib/useApi.ts";

export function Setup() {
  const query = useResource<SetupResponse>(qk.setup, () => api.setup(), { staleTimeMs: 15_000 });

  if (query.isLoading || !query.data) {
    return <PageHeader eyebrow="Setup" title="Getting started" description="Loading your setup checklist…" />;
  }

  const data = query.data;
  const allRequiredDone = data.completedCount >= data.requiredCount;

  return (
    <>
      <PageHeader
        eyebrow="Setup"
        title="Getting started"
        description={`${data.completedCount} of ${data.requiredCount} required steps done${
          data.demoMode ? " · still in demo mode" : " · live"
        }`}
      />

      {data.demoMode && allRequiredDone && data.liveReady && (
        <Notice tone="green">
          Everything required is in place. When you’re ready, turn off demo mode on the Settings screen to go live.
        </Notice>
      )}

      <Panel title="Your checklist">
        <div className="panel-body">
          {data.steps.map((step) => (
            <StepRow key={step.key} step={step} />
          ))}
        </div>
      </Panel>
    </>
  );
}

function StepRow({ step }: { step: SetupStep }) {
  return (
    <div className="setup-step">
      <span className={step.done ? "setup-mark done" : "setup-mark"}>
        {step.done && <Icon name="check" size={16} />}
      </span>
      <span className="setup-info">
        <strong>
          {step.title}
          {step.optional && <Badge tone="neutral">Optional</Badge>}
        </strong>
        <small>{step.description}</small>
      </span>
      <span className="setup-cta">
        <Badge tone={step.done ? "green" : step.optional ? "neutral" : "amber"}>
          {step.done ? "Done" : "To do"}
        </Badge>
        <Link to={step.actionPath}>
          <Button variant="ghost">{step.actionLabel}</Button>
        </Link>
      </span>
    </div>
  );
}
